﻿import { ImmigrationStatus, SSNTypes } from '../SharedEnums';
import ApplicationsEmployment from './ApplicationsEmployment';
import ApplicationsNonEmploymentIncome from './ApplicationsNonEmploymentIncome';
import ApplicationsResidence from './ApplicationsResidence';
import { DateString, DEFAULT_DATE_FORMAT_DISPLAY } from '@core/_globals/types/date-settings';
import { CustomerIdentification } from '@core/classes/CustomerIdentification';
import { formatDateToString } from '@core/services/util.date';
import util from '@core/services/util';

export default class ApplicationsPersonalInfo {
	currentEmployment?: ApplicationsEmployment = new ApplicationsEmployment();
	currentResidence = new ApplicationsResidence();
	dateOfBirth?: DateString = null;
	emailAddress?: string = null;
	employeeType?: string = null; // Possible Values: w2, 1099, or anything entered by the customer
	employmentStatus?: string = null; // Possible Values: employed, retired, disabled, student, unemployed, other
	equifaxPIN?: string = null;
	estimateCreditScore?: string = null; // Possible Values: poorCredit, fairCredit, goodCredit, veryGoodCredit, excellentCredit, dontKnowCredit
	experianPIN?: string = null;
	firstName?: string = null;
	freezeWithEquifax?: boolean = null;
	freezeWithExperian?: boolean = null;
	freezeWithTransUnion?: boolean = null;
	generationSuffix?: string = null;
	identifications: CustomerIdentification[] = [];
	immigrationStatus: ImmigrationStatus = null;
	incomeSituationDescription?: string = null;
	investment?: ApplicationsNonEmploymentIncome = new ApplicationsNonEmploymentIncome();
	lastName?: string = null;
	lastSuccessfulCreditCheck?: DateString = null;
	middleName?: string = null;
	nonEmployedStatusStartDate?: DateString = null;
	otherIncome?: ApplicationsNonEmploymentIncome = new ApplicationsNonEmploymentIncome();
	pension?: ApplicationsNonEmploymentIncome = new ApplicationsNonEmploymentIncome();
	phoneNumber?: string = null;
	prefix?: string = null;
	previousEmployment?: ApplicationsEmployment = new ApplicationsEmployment();
	previousResidence = new ApplicationsResidence();
	retirementOrDisabilityStartDate?: DateString = null;
	socialSecurity?: ApplicationsNonEmploymentIncome = new ApplicationsNonEmploymentIncome();
	ssnHashed?: string = null;
	ssnLast4?: string = null;
	ssnType?: SSNTypes = null;
	studentSchoolName?: string = null;
	suffix?: string = null;
	supplementalSecurity = new ApplicationsNonEmploymentIncome();
	transUnionPIN?: string = null;

	//calculations
	monthlyGrossSalary?: number = null;
	monthsAtAddress?: number = null;
	monthsAtEmployment?: number = null;
	monthsInNonEmployedStatus?: number = null;
	mortgageRentPayment?: number = null;
	salaryType?: string = null; //default to always Monthly
	yearsAtAddress?: number = null;
	yearsAtEmployment?: number = null;
	yearsInNonEmployedStatus?: number = null;
	constructor(init?: Partial<ApplicationsPersonalInfo>) {
		if (init) {
			this.prefix = init.prefix;
			this.firstName = init.firstName;
			this.middleName = init.middleName;
			this.lastName = init.lastName;
			this.suffix = init.suffix;
			this.generationSuffix = init.generationSuffix;
			this.dateOfBirth = init.dateOfBirth ?? null;
			this.phoneNumber = init.phoneNumber;
			this.emailAddress = init.emailAddress;
			this.immigrationStatus = init.immigrationStatus;
			this.currentResidence = new ApplicationsResidence(init.currentResidence);
			if (init.identifications && Array.isArray(init.identifications)) {
				this.identifications = init.identifications.map(a => new CustomerIdentification(a));
			} else {
				this.identifications = [];
			}
			this.ssnType = init.ssnType;
			this.ssnLast4 = init.ssnLast4;
			this.ssnHashed = init.ssnHashed;
			this.estimateCreditScore = init.estimateCreditScore;
			this.previousResidence = new ApplicationsResidence(init.previousResidence);
			this.employmentStatus = init.employmentStatus;
			this.nonEmployedStatusStartDate = init.nonEmployedStatusStartDate ?? null;
			// ? formatDateToDateObject(init.nonEmployedStatusStartDate, { formatString: DATE_FORMAT_WITH_LONG_TIME })
			this.incomeSituationDescription = init.incomeSituationDescription;
			this.employeeType = init.employeeType;
			this.currentEmployment = new ApplicationsEmployment(init.currentEmployment);
			this.previousEmployment = new ApplicationsEmployment(init.previousEmployment);
			this.socialSecurity = new ApplicationsNonEmploymentIncome(init.socialSecurity);
			this.pension = new ApplicationsNonEmploymentIncome(init.pension);
			this.supplementalSecurity = new ApplicationsNonEmploymentIncome(init.supplementalSecurity);
			this.studentSchoolName = init.studentSchoolName;
			this.investment = new ApplicationsNonEmploymentIncome(init.investment);
			this.otherIncome = new ApplicationsNonEmploymentIncome(init.otherIncome);
			this.freezeWithEquifax = init.freezeWithEquifax;
			this.equifaxPIN = init.equifaxPIN;
			this.freezeWithExperian = init.freezeWithExperian;
			this.experianPIN = init.experianPIN;
			this.freezeWithTransUnion = init.freezeWithTransUnion;
			this.transUnionPIN = init.transUnionPIN;
			this.lastSuccessfulCreditCheck = init.lastSuccessfulCreditCheck;
			this.retirementOrDisabilityStartDate = init.retirementOrDisabilityStartDate ?? null;
			// ? formatDateToDateObject(init.retirementOrDisabilityStartDate, { formatString: DATE_FORMAT_WITH_LONG_TIME })

			//calculations
			this.yearsAtAddress = init.yearsAtAddress;
			this.monthsAtAddress = init.monthsAtAddress;
			this.mortgageRentPayment = init.mortgageRentPayment;
			this.yearsAtEmployment = init.yearsAtEmployment;
			this.monthsAtEmployment = init.monthsAtEmployment;
			this.yearsInNonEmployedStatus = init.yearsInNonEmployedStatus;
			this.monthsInNonEmployedStatus = init.monthsInNonEmployedStatus;
			this.monthlyGrossSalary = init.monthlyGrossSalary;
			this.salaryType = init.salaryType;
		}
	}

	getFullName() {
		const concatenatedName = [this.prefix, this.firstName, this.middleName, this.lastName, this.suffix].filter(part => part).join(' ');
		return util.toProperCase(concatenatedName);
	}

	getDateOfBirthFormatted() {
		if (this.dateOfBirth) {
			const dob = formatDateToString(this.dateOfBirth, { formatString: DEFAULT_DATE_FORMAT_DISPLAY });
			return dob;
		}
	}

	getPhoneNumberFormatted() {
		const phone = util.formatPhoneNumber(this.phoneNumber);
		return phone;
	}

	getEmploymentStatusProperCase() {
		return util.toProperCase(this.employmentStatus);
	}

	getSalaryTypeProper() {
		return util.toProperCase(this.salaryType);
	}

	getTimeAtEmployment() {
		return `${this.yearsAtEmployment} years, ${this.monthsAtEmployment} months`;
	}

	getTimeAtNonEmployed() {
		return `${this.yearsInNonEmployedStatus} years, ${this.monthsInNonEmployedStatus} months`;
	}

	getEmploymentStatusStartDateFormatted() {
		if (this.nonEmployedStatusStartDate) {
			const movedIn = formatDateToString(this.nonEmployedStatusStartDate, { formatString: DEFAULT_DATE_FORMAT_DISPLAY });
			return movedIn;
		} else if (this.currentEmployment.startDate) {
			const movedIn = formatDateToString(this.currentEmployment.startDate, { formatString: DEFAULT_DATE_FORMAT_DISPLAY });
			return movedIn;
		}
	}

	getGrossSalaryDescription() {
		const grossSalaryDescription = [];
		if (this.employmentStatus != null) {
			//Employed Income
			if (this.employmentStatus == 'employed') {
				if (this.currentEmployment?.payAmount) {
					if (this.currentEmployment.payType == 'salary') {
						grossSalaryDescription.push('Employment Salary: $' + (this.currentEmployment.payAmount / 12).toFixed(2));
					} else {
						if (this.currentEmployment.getPaidOvertime == true && this.currentEmployment.weeklyHours > 40) {
							const overtime = (this.currentEmployment.weeklyHours - 40) * (this.currentEmployment.payAmount * 1.5);
							grossSalaryDescription.push('Employment Salary: $' + (((this.currentEmployment.payAmount * 40 + overtime) * 52) / 12).toFixed(2));
						} else {
							grossSalaryDescription.push(
								'Employment Salary: $' + ((this.currentEmployment.payAmount * this.currentEmployment.weeklyHours * 52) / 12).toFixed(2),
							);
						}
					}
				}

				//Add CommissionsOrBonuses
				if (this.currentEmployment.commissionsAndBonusesFrequency != null) {
					grossSalaryDescription.push(
						'Commissions/Bonuses Income: $' +
							util
								.calculateMonthlyIncome(this.currentEmployment.commissionsAndBonusesFrequency, this.currentEmployment.commissionsAndBonusesAmount)
								.toFixed(2),
					);
				}
			}
			//Retired Income
			else if (this.employmentStatus == 'retired' && this.socialSecurity != null) {
				const calculatedIncome = util.calculateMonthlyIncome(this.socialSecurity.frequency, this.socialSecurity.amount);

				grossSalaryDescription.push('Retirement Income: $' + (calculatedIncome ? calculatedIncome.toFixed(2) : 0));
			}
			//Disabled Income
			else if (this.employmentStatus == 'disabled' && this.supplementalSecurity != null) {
				const calculatedIncome = util.calculateMonthlyIncome(this.supplementalSecurity.frequency, this.supplementalSecurity.amount);

				grossSalaryDescription.push('Supplemental Security Income: $' + (calculatedIncome ? calculatedIncome.toFixed(2) : 0));
			}
		}

		//Add Pension
		if (this.pension?.frequency != null) {
			grossSalaryDescription.push('Pension Income: $' + util.calculateMonthlyIncome(this.pension.frequency, this.pension.amount).toFixed(2));
		}

		//Add Investments
		if (this.investment?.frequency != null) {
			grossSalaryDescription.push('Investments Income: $' + util.calculateMonthlyIncome(this.investment.frequency, this.investment.amount).toFixed(2));
		}

		//Add Other Income
		if (this.otherIncome?.frequency != null) {
			grossSalaryDescription.push('Other Income: $' + util.calculateMonthlyIncome(this.otherIncome.frequency, this.otherIncome.amount).toFixed(2));
		}

		return grossSalaryDescription;
	}

	getMonthlyGrossSalaryProper() {
		const formatted = util.formatPrice(this.monthlyGrossSalary);
		return formatted;
	}
}
